import { z } from 'zod';
import { cardListRefSchema } from './card-detail';
import { ulidSchema } from './id';
import { publicProfileSchema } from './user';

/**
 * Attachments, comment attachments and related cards — the contract layer for
 * block **B27**.
 *
 * `BE-27.1` (`FR-CARD-022` attach a file from the device, `FR-CARD-024`
 * rename / download / remove), `BE-27.2` (the authorised download, SC-3),
 * `BE-27.3` (`FR-CARD-023` attach a link, including from cloud storage),
 * `BE-27.4` (`FR-COLLAB-005` attach a file, card or board to a comment) and
 * `BE-27.5` (`FR-TMPL-028` related cards).
 *
 * ---------------------------------------------------------------------------
 * ONE CONTRACT FILE, THREE ROUTE FILES — ONE FURTHER THAN `./board-archive`
 * ---------------------------------------------------------------------------
 * `./search` is one route file and one contract file; `./cross-board-move` is
 * two routes sharing one contract file because they share their whole report;
 * `./board-archive` is the first with **two route files** sharing one contract
 * because they describe one lifecycle from its two ends. This is the fourth
 * combination and it is **three** route files —
 * `apps/server/src/routes/attachments.ts` (the attachment's own five routes),
 * `apps/server/src/routes/card-relations.ts` (`FR-TMPL-028`) and
 * `apps/server/src/routes/comment-attachments.ts` (`FR-COLLAB-005`) — kept in
 * one contract because **all three converge on the same `attachments` row**.
 * `FR-COLLAB-005` says a comment's file attachment "also joins the card's
 * attachment section", which is not a resemblance but the same table: one
 * `attachments` insert, one `attachment.added` event, one appearance in
 * `GET /cards/:id/attachments`. Splitting the comment half into its own
 * contract file would put two declarations of one row in two places, which is
 * the drift `./board-contents` refuses when it extends `listSchema` on a copy
 * rather than restating a field.
 *
 * Related cards are the one member of this file that touches a different table
 * (`card_relations`), and they are here rather than in a file of their own for
 * the opposite reason: `FR-TMPL-028` presents a related card to the user as an
 * attachment ("A card can be added as an ATTACHMENT to another card"), so the
 * two shapes are read side by side on one card back and a client assembling
 * that section needs both. ⚠ **The product framing is the only thing they
 * share.** See `relatedCardSchema` for why they are two tables, two vocabularies
 * and two event prefixes, and `db/postgres/migrations/0022_attachment_
 * activity.sql` for the collision that framing causes.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS FILE DECLARES NO NOUN OF `BE-02.1`'s EIGHT — THE TRAP `./index`'s
 * HEADER HAS NOW SPRUNG SIX TIMES, SAID BEFORE THE COUNT GOES WRONG
 * ---------------------------------------------------------------------------
 * Like `./board-filter` (a parameter's contract), `./search`, `./cross-board-
 * move` and `./board-archive`, this is **three routes' request and response
 * contracts and nothing more**. "Attachment" is not one of `BE-02.1`'s eight
 * nouns (workspace, board, list, card, comment, label, membership,
 * notification). Seven of the eight have a file; **membership** is still the
 * one that does not, and `ls packages/shared/src` is still the instruction.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `url` IS ON THE WIRE FOR A LINK AND NEVER FOR A FILE, AND THAT IS THE
 * WHOLE OF SC-3 EXPRESSED IN A SCHEMA
 * ---------------------------------------------------------------------------
 * `attachments.url` is `varchar(2048) NOT NULL` and it is **overloaded by
 * `kind`** — the column has held both meanings since `0003_card_detail.sql`
 * and there is no second column to separate them:
 *
 *   * `kind = 'link'` → the URL the member pasted, or the one a cloud picker
 *     returned. It goes on the wire **verbatim**. It is somebody else's URL,
 *     the product fetches nothing, and `FR-CARD-023` AC4 makes the provider's
 *     own access control the only gate ("the card does not expose the
 *     contents").
 *   * `kind = 'file'` → the **S3 object key**. It is NOT a URL, it never
 *     crosses the wire, it never enters an `activity_entries` payload, and the
 *     only way to fetch the object is `GET /attachments/:id/download`, which
 *     permission-checks and then `302`s to a presigned GET valid for about
 *     sixty seconds.
 *
 * That is why `attachmentSchema` below is a `z.discriminatedUnion('kind', …)`
 * — `./card-cover`'s shape one table over — and not a `z.object` with `url`
 * optional. A `z.object` makes the file variant's absent URL a **convention**
 * that any later projection can quietly break; the union makes it
 * **inexpressible**, so a handler that tried to serialise a storage key would
 * not compile. `FR-CARD-022` AC5 ("a user who is not a member of the card's
 * board holds a direct attachment URL … access is denied") is unsatisfiable
 * against a payload that hands out the key, no matter how the download route
 * is written.
 *
 * ⚠ **The file variant carries no download URL either — not even the app's
 * own.** `GET /attachments/:id/download` is derivable from `id`, and a second
 * encoding of a fact another field already carries is what B21 refused for
 * `direction` and B25 for `cover_type`. A client builds the path.
 *
 * ⚠ **And no `thumbnailUrl`, on either variant.** `attachments.thumbnail_url`
 * has existed since `0003` and B27 writes it **NULL** on every row.
 * `FR-CARD-022` says "image uploads generate a thumbnail preview", which needs
 * an image pipeline this workspace has no dependency for — and, more to the
 * point, a stored URL for a thumbnail OF A PRIVATE FILE is exactly the SC-3
 * violation the union above exists to prevent, in miniature. A later block
 * that adds thumbnails serves them through the same authorised route (a
 * variant parameter on the download), never through a column on this view. A
 * client that wants an image preview today calls the download route.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `sizeBytes` IS A `bigint` COLUMN CROSSING A `z.number()` WIRE, AND THE
 * CAST IS TIED TO `ATTACHMENT_MAX_BYTES`
 * ---------------------------------------------------------------------------
 * `attachments.size_bytes` is `bigint NULL`. node-postgres hands a `bigint`
 * back as a **string**, so a handler that selects the column raw and validates
 * it against `z.number()` **500s the happy path** on the first file anybody
 * uploads. The server therefore casts in SQL — `${attachments.sizeBytes}::int`
 * — which is the same instrument `count(*)::int` uses throughout
 * `./board-contents`, and `NULL::int` is `NULL`, so the nullable link case
 * survives it untouched.
 *
 * ⚠ **The cast is only safe because of the constant below, and the two must be
 * read together.** `ATTACHMENT_MAX_BYTES` is 262,144,000 — a factor of eight
 * under `int4`'s 2,147,483,647 ceiling — so no row this product can create
 * overflows the cast. If a later block **raises** the cap past about 2 GB, this
 * cast becomes `22003 integer out of range`: a 500 on the card back and on the
 * board face, not a validation error, and not on the row being written but on
 * every read that touches it afterwards. The fix at that point is `::bigint`
 * plus a string on the wire, and it is a breaking change to this contract —
 * which is the reason the dependency is written down here rather than
 * discovered. (`FR-CARD-022`'s own edge case anticipates the cap moving:
 * "lowering the size cap in future must not delete attachments already
 * uploaded above it". Lowering is safe; raising is the direction that bites.)
 *
 * ---------------------------------------------------------------------------
 * ⚠ TIMESTAMPS ARE `z.string().min(1)`, NEVER `z.iso.datetime()`
 * ---------------------------------------------------------------------------
 * `BE-24.1`'s standing note, inherited rather than re-derived: a `timestamptz`
 * comes back from this driver in Postgres' own spelling —
 * `2026-09-01 09:00:00+00`, a space rather than a `T` and no `Z` — which
 * `z.iso.datetime()` **refuses**. The view fields below are bounded strings for
 * that reason and for no other; they are not free text.
 */

// ---------------------------------------------------------------------------
// Bounds. `./card`'s rule: import the constant that describes YOUR column if
// one exists, declare it if none does, and never share one across two columns.
// Both branches are taken in this file — `ATTACHMENT_DISPLAY_NAME_MAX_LENGTH`
// describes `attachments.display_name`, a column no existing constant covers,
// and `ATTACHMENT_URL_MAX_LENGTH` describes `attachments.url`. Neither reuses
// `CARD_DESCRIPTION_MAX_LENGTH` or `COMMENT_BODY_MAX_LENGTH`, which are the
// same *kind* of bound on different columns.
// ---------------------------------------------------------------------------

/**
 * `FR-CARD-022`: "Per-file upload size is capped at 250 MB." 250 × 1024 × 1024
 * = 262,144,000 bytes.
 *
 * ⚠ **It bounds an UPLOAD, and it is enforced in two places that fail
 * differently.** The presign route refuses a larger declared size with a `422`
 * naming the field (`FR-CARD-022` AC1: "it is rejected and the 250 MB limit is
 * stated" — so the message must name the limit, not merely refuse). That check
 * is advisory on its own: a client that lies about the size still gets a signed
 * URL. What actually binds is that the presigned `PUT` is signed **with the
 * declared content length**, so an object of a different size is rejected by
 * the object store itself before a byte reaches this product — and the confirm
 * route then reads the real length back from the store rather than from the
 * request body. ⚠ Drop the signed length and the 250 MB cap becomes a number in
 * a docstring: the upload succeeds, the row records whatever the client claimed,
 * and nothing is red anywhere.
 *
 * ⚠ **It does NOT bound a link.** `FR-CARD-023` AC3 is explicit — "given a link
 * attachment to a 2 GB cloud file, when it is attached, then no size limit is
 * applied because no file is uploaded" — which is why `sizeBytes` is absent
 * from the link create body entirely rather than present and optional.
 *
 * ⚠ **And it is not retroactive.** `FR-CARD-022`'s edge case: "lowering the size
 * cap in future must not delete attachments already uploaded above it, only
 * block new ones." Nothing on the read path compares a stored `sizeBytes`
 * against this number, and nothing should.
 *
 * See the module header for why raising this constant past ~2 GB is a breaking
 * change to `attachmentSchema.sizeBytes`.
 */
export const ATTACHMENT_MAX_BYTES = 250 * 1024 * 1024;

/** `attachments.display_name` — `varchar(255)`, read from the live catalogue. */
export const ATTACHMENT_DISPLAY_NAME_MAX_LENGTH = 255;

/**
 * `attachments.url` — `varchar(2048)`. Bounds the pasted URL of a link and the
 * storage key of a file, because it is one overloaded column (see the module
 * header).
 */
export const ATTACHMENT_URL_MAX_LENGTH = 2048;

/**
 * `ck_attachments_cloud_provider` restated. Nullable on the row; `null` is the
 * ordinary answer, and it is what every attachment in this product carries
 * until a cloud picker exists to say otherwise.
 *
 * ⚠ **B27 persists this value and builds no integration.** `SG-7` records that
 * attachment source restrictions have no owner and must not be invented, and
 * `FR-CARD-023` AC2's picker is `FE-27.2`'s. The server accepts the provider
 * name a client claims, stores it, and renders it back; it authenticates
 * nothing against the provider, fetches nothing and refreshes nothing. A reader
 * who expects OAuth here is looking for a block that does not exist.
 */
export const ATTACHMENT_CLOUD_PROVIDERS = [
  'google_drive',
  'dropbox',
  'box',
  'onedrive',
] as const;

export const attachmentCloudProviderSchema = z.enum(ATTACHMENT_CLOUD_PROVIDERS);

export type AttachmentCloudProvider = z.infer<typeof attachmentCloudProviderSchema>;

// ---------------------------------------------------------------------------
// VIEWS — what the routes RETURN.
// ---------------------------------------------------------------------------

/**
 * The fields both attachment variants carry. Declared once and spread into each
 * member of the union below, because `z.discriminatedUnion` needs the
 * discriminator on a literal in every member and there is no `.extend()` across
 * a union — `./card-cover`'s `cardCoverSchema` repeats its shared fields for the
 * same reason, and this file factors them instead only because there are six of
 * them rather than two.
 */
const attachmentCommonShape = {
  id: ulidSchema,
  /**
   * `attachments.display_name`. `FR-CARD-023` AC1 lets the member choose it for
   * a link; for a file it is the original filename unless renamed. Bounded, not
   * validated further: it is a label, and `FR-CARD-024` AC3 puts the member in
   * charge of it.
   */
  displayName: z.string().max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH),
  /**
   * `attachments.uploaded_by_user_id`, resolved. **Nullable**, and the reason is
   * a foreign key rather than a product rule:
   * `fk_attachments_uploaded_by_user_id` is `ON DELETE SET NULL`, so an
   * attachment outlives the account that uploaded it. `FR-CARD-024`'s list must
   * not lose rows because somebody left the organisation.
   */
  uploadedBy: publicProfileSchema.nullable(),
  /** `attachments.created_at`. See the module header on the string form. */
  createdAt: z.string().min(1),
  /**
   * `attachments.updated_at`, owned by `trg_attachments_set_updated_at` — no
   * handler in this block writes it. It moves on a rename and on nothing else,
   * which is what lets a client tell a renamed attachment from an untouched one
   * without reading the feed.
   */
  updatedAt: z.string().min(1),
} as const;

/**
 * One attachment on a card — `FR-CARD-022`, `FR-CARD-023`, `FR-CARD-024`.
 *
 * A `z.discriminatedUnion('kind', …)` over `ck_attachments_kind`'s own two
 * members, so `{ kind: 'file', url: … }` is a value that **cannot be
 * constructed**, let alone serialised. See the module header for the full
 * argument; the one-line version is that SC-3 is a property of the schema here,
 * not of handler discipline.
 */
export const attachmentSchema = z.discriminatedUnion('kind', [
  z.object({
    ...attachmentCommonShape,
    kind: z.literal('file'),
    /**
     * `attachments.size_bytes`, cast `::int` in SQL. Nullable on the column and
     * therefore here, although B27 always writes a value for a file: the confirm
     * route reads the real object length back from the store. See the module
     * header for the cast's dependency on `ATTACHMENT_MAX_BYTES`.
     */
    sizeBytes: z.number().int().nonnegative().nullable(),
    /**
     * ⚠ **There is deliberately no `url` and no `downloadUrl` on this variant.**
     * The storage key is private (SC-3) and the download path is derivable from
     * `id`. `GET /attachments/:id/download` is the only way to reach the bytes.
     */
  }),
  z.object({
    ...attachmentCommonShape,
    kind: z.literal('link'),
    /**
     * `attachments.url`, verbatim. The member pasted it or a provider returned
     * it; the product fetches nothing and validates nothing beyond the shape on
     * the way in. `FR-CARD-023` AC4 makes the target's own access control the
     * only gate.
     */
    url: z.string().max(ATTACHMENT_URL_MAX_LENGTH),
    /**
     * `attachments.cloud_provider`. `null` for an ordinary pasted URL; a
     * provider name when a picker supplied it. Persisted, never authenticated —
     * see `ATTACHMENT_CLOUD_PROVIDERS` and `SG-7`.
     */
    cloudProvider: attachmentCloudProviderSchema.nullable(),
    /**
     * ⚠ **No `sizeBytes` on this variant at all**, rather than a nullable one.
     * `FR-CARD-023` AC3: "no size limit is applied because no file is uploaded".
     * A nullable field here would invite a client to render "unknown size" for a
     * thing that has no size in this product's model.
     */
  }),
]);

export type Attachment = z.infer<typeof attachmentSchema>;

/**
 * `GET /cards/:id/attachments` — `FR-CARD-024`, "attachments are ordered
 * most-recent-first by default".
 *
 * ⚠ **Unpaged, deliberately, and this is the one place B27 departs from
 * `./comment`'s reasoning about an unbounded collection.** `FR-CARD-022`
 * records that "Trello documents no per-card count limit", which is the same
 * sentence `FR-COLLAB-001` produced for comments — and comments got a keyset
 * cursor while attachments do not. The difference is what the requirement then
 * asks for: `FR-CARD-024` requires "next/previous navigation across the card's
 * attachments" in the preview, which is a client-side traversal of the WHOLE
 * set. A paged list makes that gesture cross a network boundary at an arbitrary
 * page edge, and there is no ordering a cursor could use that also matches the
 * preview's order. Comments arrive conversationally and are read newest-first
 * without ever needing the whole stream; attachments arrive one deliberate
 * human action at a time and are read as a set.
 *
 * If a cap is ever needed it is `ARCHIVE_PAGE_SIZE_DEFAULT`'s shape
 * (`./board-archive`) and adding it is additive to this contract — an array
 * becoming `{ items, nextCursor }` is not, so a later block should add a
 * parameter rather than reshape this response.
 */
export const attachmentListSchema = z.array(attachmentSchema);

export type AttachmentList = z.infer<typeof attachmentListSchema>;

/**
 * `POST /cards/:id/attachments/presign` — `BE-27.1`, SC-3.
 *
 * ⚠ **This is the only response in this package that carries a credential**,
 * and its two bounds are what keep that acceptable: the URL is a `PUT` target
 * for one object key that does not exist yet, and it expires. It grants no read
 * of anything and names no existing row.
 */
export const attachmentPresignSchema = z.object({
  /**
   * The opaque object key the client must hand back to
   * `POST /cards/:id/attachments`. ⚠ **The confirm route must verify this key
   * belongs to the card in its own path** — see `attachmentCreateBodySchema`.
   */
  storageKey: z.string().min(1).max(ATTACHMENT_URL_MAX_LENGTH),
  /** The presigned `PUT` target. Sign the content length into it — see
   * `ATTACHMENT_MAX_BYTES`. */
  url: z.string().min(1),
  /**
   * The headers the client MUST send with the `PUT`, byte for byte. A presigned
   * URL signed over `Content-Length` and `Content-Type` rejects a request that
   * omits or alters either, with a signature error rather than a helpful one —
   * so they are handed over explicitly rather than left for a client to infer.
   */
  headers: z.record(z.string(), z.string()),
  /** Seconds until the signature expires. */
  expiresInSeconds: z.number().int().positive(),
});

export type AttachmentPresign = z.infer<typeof attachmentPresignSchema>;

// ---------------------------------------------------------------------------
// INPUTS — what the routes ACCEPT. All `z.strictObject`: an unknown key is
// REFUSED with a 422 naming it, never silently dropped.
// ---------------------------------------------------------------------------

/**
 * `POST /cards/:id/attachments/presign` — `BE-27.1`, `FR-CARD-022`.
 *
 * Writes nothing anywhere. It signs a `PUT` and returns a key; an upload that
 * is presigned and PUT but never confirmed leaves an S3 object and **no row**,
 * which is `FR-COLLAB-005` AC3's promise ("attach a file and then cancel the
 * comment before posting … the file is not added to the card") arriving on the
 * card surface. The orphaned object is a recorded cost, not a defect — bucket
 * lifecycle has no owner in the corpus (B01, `SG-7`) — and it must not be
 * "fixed" by an S3 delete inside a transaction.
 */
export const attachmentPresignBodySchema = z.strictObject({
  /**
   * The original filename, which becomes `display_name` on confirm unless the
   * body overrides it. Not a path: any directory separator a client sends is
   * part of the label and never part of the key, which the server derives from
   * the card id and a fresh ULID.
   */
  fileName: z.string().min(1).max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH),
  /**
   * The declared length, in bytes. Refused above `ATTACHMENT_MAX_BYTES` with a
   * `422` whose message states the limit (`FR-CARD-022` AC1 requires the limit
   * to be stated, not merely the upload refused).
   *
   * ⚠ **This number is signed into the presigned URL and is not trusted
   * afterwards.** The confirm route reads the object's real length back from the
   * store. See `ATTACHMENT_MAX_BYTES`.
   */
  sizeBytes: z.number().int().nonnegative().max(ATTACHMENT_MAX_BYTES),
  /**
   * The `Content-Type` the client will send, signed into the URL alongside the
   * length. Bounded and otherwise unvalidated: `SG-7` records that attachment
   * source and type restrictions have no owner, so a type allow-list here would
   * be invented policy. It is a label the download route echoes back, never a
   * decision.
   */
  contentType: z.string().min(1).max(255),
});

export type AttachmentPresignBody = z.infer<typeof attachmentPresignBodySchema>;

/**
 * `POST /cards/:id/attachments` — `BE-27.1` (confirm a file upload) **and**
 * `BE-27.3` (create a link attachment). One route, one table, one event, two
 * shapes discriminated on `kind` — `./card-cover`'s `cardCoverPutSchema` is the
 * shipped precedent that a `z.discriminatedUnion` of `z.strictObject`s compiles
 * into a JSON Schema Fastify's `ajv` instance accepts.
 *
 * Two routes would have been the alternative and was rejected: `FR-CARD-023`'s
 * link and `FR-CARD-022`'s file produce the **same row in the same table** and
 * are listed in the same section of the card back, so a split would give one
 * table two creation paths, two guards to keep identical, and two places for
 * `attachment.added`'s payload to drift.
 */
export const attachmentCreateBodySchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('file'),
    /**
     * The key `POST /cards/:id/attachments/presign` returned.
     *
     * ⚠⚠ **The handler MUST verify this key is prefixed for the card in its own
     * path.** A `ulidSchema`-shaped check cannot ask that question, exactly as
     * `addCardMemberBodySchema.userId` cannot ask whether an id names a board
     * member. Without the check, a caller who has legitimately presigned an
     * upload on card A confirms it against card B and the download route then
     * serves that object under card B's permissions — a cross-card read grant
     * that no status code, response field or schema can express, on a request
     * that is valid in every other respect.
     *
     * ⚠ **And the handler MUST `HeadObject` before opening the transaction**,
     * both to prove the `PUT` actually happened (`FR-CARD-022` AC2: the
     * attachment is listed "when the upload completes") and to take
     * `size_bytes` from the store rather than from any client. An S3 call inside
     * `withTransaction` couples a commit to a second system that cannot be
     * rolled back — `publishEvents`' reason, which is why it sits outside too.
     */
    storageKey: z.string().min(1).max(ATTACHMENT_URL_MAX_LENGTH),
    /**
     * Required-but-nullable, the house shape for "the field must be addressed
     * and `null` is one of its legal answers" (`cardDueDatePatchSchema.dueAt`).
     * `null` means "use the filename the presign was issued for". There is no
     * `.optional()` here for the reason no `z.strictObject` in this package has
     * one: an omitted key and a `null` key must not be two spellings of one
     * intention.
     */
    displayName: z.string().min(1).max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH).nullable(),
    /**
     * ⚠ **No `sizeBytes` here.** It would be a number nobody verified, and
     * `FR-CARD-022`'s 250 MB cap enforced against it would be enforced against
     * nothing. The store is the authority.
     */
  }),
  z.strictObject({
    kind: z.literal('link'),
    /**
     * The URL, verbatim. ⚠ **Bounded and otherwise unvalidated beyond a scheme
     * check the handler owns**: `FR-CARD-023` says "an arbitrary URL", and
     * `SG-7` forbids inventing source restrictions. What the handler must
     * refuse is a `javascript:` or `data:` scheme, which is not a source
     * restriction but the difference between a link and a script — the card back
     * renders this value into an anchor for every board member.
     */
    url: z.string().min(1).max(ATTACHMENT_URL_MAX_LENGTH),
    /**
     * `FR-CARD-023` AC1: "the user pastes a URL and provides a display name,
     * then a link attachment appears using that display name." `null` falls back
     * to the URL itself, which is what a bare paste should show.
     */
    displayName: z.string().min(1).max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH).nullable(),
    /**
     * `FR-CARD-023` AC2. Persisted as claimed; nothing is authenticated against
     * the provider. See `ATTACHMENT_CLOUD_PROVIDERS`.
     */
    cloudProvider: attachmentCloudProviderSchema.nullable(),
  }),
]);

export type AttachmentCreateBody = z.infer<typeof attachmentCreateBodySchema>;

/**
 * `PATCH /attachments/:id/display-name` — `FR-CARD-024` AC3, `BE-27.1`.
 *
 * Field-scoped per SC-4, and the path segment names the **column** rather than
 * the gesture: `PATCH /me/display-name` (B06) is the shipped precedent for this
 * exact field name, and `/name` would have made the path and the body key
 * disagree for no gain.
 *
 * ⚠ **A rename to the identical string writes nothing and emits nothing**,
 * decided against the pre-image inside the transaction. A rename dialog
 * pre-filled with the current name and submitted unchanged is the ordinary
 * accident; emitting on it fills an append-only feed with phantom renames and
 * passes every visible assertion. `board.permission_changed`'s no-op rule.
 */
export const attachmentRenameBodySchema = z.strictObject({
  displayName: z.string().min(1).max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH),
});

export type AttachmentRenameBody = z.infer<typeof attachmentRenameBodySchema>;

// ---------------------------------------------------------------------------
// RELATED CARDS — `FR-TMPL-028`, `BE-27.5`, the `card_relations` table.
// ---------------------------------------------------------------------------

/**
 * One related card as it appears on a card's back — `FR-TMPL-028` AC1: "card X
 * shows an attachment displaying card Y's title and its board and list."
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ A RELATED CARD IS NOT AN ATTACHMENT, AND THE REQUIREMENT'S OWN PROSE SAYS
 * OTHERWISE
 * ---------------------------------------------------------------------------
 * `FR-TMPL-028` is titled "Attach a card to another card" and its behaviour
 * text reads "A card can be added as an ATTACHMENT to another card". Read as
 * product language that is exactly right, and it is why this schema lives in
 * this file. Read as a data model it is wrong in three ways that all bite:
 *
 *   * It is a `card_relations` row — its own table, its own
 *     `uq_card_relations_from_card_id_to_card_id`, its own
 *     `ck_card_relations_self` refusing a card attached to itself — and **no
 *     `attachments` row exists anywhere**. So it is absent from
 *     `GET /cards/:id/attachments` and from `FR-CARD-022` AC4's count badge,
 *     both of which read `attachments`.
 *   * Its events are `card.relation_added` / `card.relation_removed`, **not**
 *     `attachment.added`. `db/postgres/migrations/0022_attachment_activity.sql`
 *     argues that at length: `attachment.added`'s payload names an `attachments`
 *     row this fact does not have, and reusing the name would force `B28` to
 *     switch on `payload` to learn which table a type refers to.
 *   * It has no size, no URL, no upload and no download.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `accessible: false` IS `FR-COLLAB-005`'s EDGE CASE APPLIED BY ANALOGY,
 * BECAUSE `FR-TMPL-028` HAS NONE OF ITS OWN
 * ---------------------------------------------------------------------------
 * `FR-COLLAB-005`'s edge case is verbatim: "a card chip for a card the viewer
 * cannot access renders as a generic 'a card on a board you don't have access
 * to' placeholder, never leaking the title." `FR-TMPL-028` says nothing about
 * the case, and it is reachable there for exactly the same reason it is
 * reachable in a comment — a relation may point at a card on another board
 * (the requirement renders "the target card's title, BOARD, list and status",
 * so a different board is expected), and board membership is per board. The
 * analogy is recorded rather than assumed, so that a later reader does not
 * "simplify" this union into a flat shape with nullable fields and reintroduce
 * the leak: **a nullable `title` and an `accessible` boolean are the same data
 * only until somebody forgets the boolean.**
 *
 * The server resolves accessibility per row, at read time, against the caller.
 * The event payloads carry ids and never titles for the same reason.
 */
export const relatedCardSchema = z.discriminatedUnion('accessible', [
  z.object({
    accessible: z.literal(true),
    /** `card_relations.to_card_id`. */
    id: ulidSchema,
    title: z.string(),
    /** `FR-TMPL-028` AC1's "board", by id and name. */
    boardId: ulidSchema,
    boardTitle: z.string(),
    /** `FR-TMPL-028` AC1's "list". `cardListRefSchema` is `./card-detail`'s,
     * imported rather than restated, so `archived` means what it means there.
     *
     * ⚠⚠ **`z.lazy(() => cardListRefSchema)`, not a direct reference — a
     * module-cycle fix, not decoration.** `./card-detail` imports
     * `attachmentListSchema` from this file for its own `cardDetailSchema.
     * attachments` (`BE-27.x`), which makes this module and `./card-detail`
     * import each other. A direct reference here is fine when `./card-detail`
     * happens to finish loading `cardListRefSchema` before this file is
     * evaluated, and silently resolves to `undefined` — no error, a
     * `relatedCardSchema` that is missing its `list` shape entirely — when the
     * load order runs the other way, which depends only on which module a
     * caller imports first. `./card-detail.ts`'s own `attachments` field
     * carries the measured proof and the fuller argument; this is the same
     * fix applied to the other half of the same cycle. Do not revert either
     * side on its own — reverting one and not the other reopens the failure
     * in the direction that currently happens not to be tested.
     */
    list: z.lazy(() => cardListRefSchema),
    /**
     * `FR-TMPL-028` AC4: "given card Y is archived, when I view card X, then the
     * attachment reflects that card Y is archived." The card's OWN
     * `archived_at`, never its list's — B22's gate, and the two are different
     * facts (`list.archived` on the ref above carries the other one).
     */
    archived: z.boolean(),
    /**
     * Whether the mirror row `(to_card_id, from_card_id)` also exists —
     * `FR-TMPL-028` AC2's "connect cards" result. **Computed per row**, because
     * either side can be removed independently afterwards and the flag recorded
     * at creation time would go stale. This is what `FE-27.4` renders the
     * requirement's asymmetry from.
     */
    reciprocal: z.boolean(),
  }),
  z.object({
    accessible: z.literal(false),
    /**
     * The id and nothing else. Not a leak: the row is on THIS card's board and
     * a member of this board put it there. The title, board name and list are
     * withheld — that is the whole point of the variant.
     */
    id: ulidSchema,
    reciprocal: z.boolean(),
  }),
]);

export type RelatedCard = z.infer<typeof relatedCardSchema>;

/**
 * `GET /cards/:id/relations` — the from-side only.
 *
 * ⚠ **Rows where this card is `to_card_id` are NOT included**, and that is
 * `FR-TMPL-028`'s edge case rather than an omission: "removing an attachment on
 * one side does not remove the reciprocal one; that asymmetry should be
 * surfaced to the actor." Including the reverse direction would make the two
 * sides look symmetric — a card would show a chip for a relation it does not
 * hold, and removing it from here would fail or would reach across into another
 * card's data. The asymmetry is surfaced through `reciprocal` on each row
 * instead.
 */
export const relatedCardListSchema = z.array(relatedCardSchema);

export type RelatedCardList = z.infer<typeof relatedCardListSchema>;

/**
 * `POST /cards/:id/relations` — `FR-TMPL-028`, `BE-27.5`.
 *
 * ⚠ **`reciprocal` is required, not defaulted.** `FR-TMPL-028` describes two
 * distinct gestures — attaching a card, and "connect cards" — and a default
 * would make one of them the silent outcome of an under-specified request.
 * `cardCoverPutSchema`'s argument against `.default()`, one table over.
 *
 * ⚠ **`reciprocal: true` writes TWO rows and emits `card.relation_added`
 * TWICE**, once per card, on two different boards. That is two facts, not one
 * fact with two audiences — see `card.relation_added`'s entry in `./events`,
 * which contrasts it with B21's `card.moved_between_boards` double-write in
 * full.
 *
 * ⚠ **Guards: the caller must be able to WRITE the card in the path and READ
 * the target; `reciprocal: true` additionally requires WRITE on the target**,
 * because the mirror row is a change to the target card. A caller who may read
 * but not write the target gets a `403` naming `reciprocal` and **no** row is
 * written at all — one transaction, all or nothing — rather than a partial
 * success the client would have to reconcile.
 *
 * ⚠ **A self-relation is refused with a `422`, not left to the database.**
 * `ck_card_relations_self` would raise `23514`, which surfaces as a 500;
 * `FR-TMPL-028`'s edge case ("attaching a card to itself must be refused") asks
 * for a refusal a client can render.
 */
export const cardRelationCreateBodySchema = z.strictObject({
  toCardId: ulidSchema,
  reciprocal: z.boolean(),
});

export type CardRelationCreateBody = z.infer<typeof cardRelationCreateBodySchema>;

// ---------------------------------------------------------------------------
// COMMENT ATTACHMENTS — `FR-COLLAB-005`, `BE-27.4`, the `comment_attachments`
// table.
// ---------------------------------------------------------------------------

/**
 * A linked board inside a comment — `FR-COLLAB-005`, `kind='board_link'`.
 *
 * `relatedCardSchema`'s union one noun over, declared separately because a board
 * has no list, no archived flag and no reciprocal. Kept as a discriminated union
 * rather than an inline `.or()` so both link shapes in this file read the same
 * way and neither can drift into a flat nullable shape.
 */
export const boardLinkRefSchema = z.discriminatedUnion('accessible', [
  z.object({
    accessible: z.literal(true),
    id: ulidSchema,
    title: z.string(),
  }),
  z.object({
    accessible: z.literal(false),
    /** The id alone. The title is withheld — that is the whole point. */
    id: ulidSchema,
  }),
]);

export type BoardLinkRef = z.infer<typeof boardLinkRefSchema>;

/**
 * ⚠⚠ **THE ONE-TARGET-PER-ROW INVARIANT IS NOT ENFORCED BY THE DATABASE, AND
 * CANNOT BE. NO HANDLER MAY TRUST IT.**
 *
 * `comment_attachments` has three nullable target columns — `attachment_id`,
 * `linked_card_id`, `linked_board_id` — and a `kind` that says which one should
 * be populated. `db/postgres/migrations/0004_collaboration_and_activity.sql`
 * declares `ck_comment_attachments_kind` (the vocabulary) and **deliberately
 * does not declare `ck_comment_attachments_target`** ("exactly one target,
 * matching kind"). Read that file's own comment; the two reasons it gives are:
 *
 *   1. MySQL 8 raises errno 3823 for a CHECK over a column that a foreign key's
 *      referential action writes to, and all three targets are `ON DELETE SET
 *      NULL`. There is no ordering of the DDL that works. Postgres has no such
 *      restriction, but the constraint is kept out for parity with the source
 *      schema rather than re-derived.
 *   2. **The constraint would contradict the requirement anyway.** `SET NULL`
 *      *is* the `FR-COLLAB-005` behaviour — AC4: "when I delete the card's
 *      attachment from the attachment section, then the comment shows the
 *      attachment as removed rather than rendering a broken preview" — so a
 *      surviving `kind='file'` row with a `NULL attachment_id` is the CORRECT
 *      steady state, and any "exactly one target" CHECK would refuse it.
 *
 * Two consequences, and both are load-bearing:
 *
 *   * **On the way in**, the application is the only thing that enforces one
 *     target per row. The insert must set exactly the column `kind` names and
 *     leave the other two NULL. A handler that trusts a constraint here will
 *     write a row with two targets, or none, and nothing will complain.
 *   * **On the way out**, a row whose target has been nulled is NORMAL and must
 *     render as removed. That is why every variant below is nullable in its
 *     target rather than the row being omitted — omitting it would lose AC4's
 *     "shows the attachment as removed" and leave the comment silently shorter
 *     than it was.
 */
export const commentAttachmentSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('file'),
    id: ulidSchema,
    /**
     * The `attachments` row this comment attachment points at, or `null` once
     * `DELETE /attachments/:id` has removed it (`fk_comment_attachments_
     * attachment_id` is `ON DELETE SET NULL`). `null` is `FR-COLLAB-005` AC4's
     * "shows the attachment as removed", not an error.
     */
    attachment: attachmentSchema.nullable(),
  }),
  z.object({
    kind: z.literal('card_link'),
    id: ulidSchema,
    /**
     * `FR-COLLAB-005` AC2: "it shows a card chip with that card's title and its
     * current list, not a bare URL" — and its edge case, which
     * `relatedCardSchema` above quotes in full, requires an inaccessible card to
     * render as a placeholder. Same union, same reason; `null` here means the
     * linked card was hard-deleted (the FK's `SET NULL`), which is a different
     * fact from `accessible: false` and must not be collapsed into it.
     */
    card: relatedCardSchema.nullable(),
  }),
  z.object({
    kind: z.literal('board_link'),
    id: ulidSchema,
    /**
     * `FR-COLLAB-005`'s "or links another card or board". Same
     * `accessible`-discriminated union as `relatedCardSchema` rather than a
     * flat shape with a nullable `title`, and for the identical reason
     * recorded there: a board the viewer cannot open must render as a
     * placeholder and must never leak its name, and a nullable field plus a
     * boolean is the same data only until somebody forgets the boolean. `null`
     * on the whole field means the linked board was deleted
     * (`fk_comment_attachments_linked_board_id` is `ON DELETE SET NULL`), which
     * is a different fact from `accessible: false` and must not be collapsed
     * into it.
     */
    board: boardLinkRefSchema.nullable(),
  }),
]);

export type CommentAttachment = z.infer<typeof commentAttachmentSchema>;

/**
 * `POST /comments/:id/attachments` — `FR-COLLAB-005`, `BE-27.4`.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ TWO GUARDS, AND WHICH ONES DEPENDS ON `kind`
 * ---------------------------------------------------------------------------
 * The base guard is `requireCommentAuthor` — B16's, and the same one
 * `PATCH /comments/:id/body` takes. Attaching to somebody else's comment would
 * put words in their mouth, and B16 already ruled that editing a comment is an
 * **authorship** check that never consults the board's commenting ladder.
 *
 * ⚠ **`kind: 'file'` additionally requires `requireCardWriter` on the comment's
 * card, and this is not belt-and-braces.** A file attachment creates an
 * `attachments` row that JOINS THE CARD'S ATTACHMENT SECTION
 * (`FR-COLLAB-005`), which is a card-level write. Authorship alone does not
 * imply it: `requireBoardCommenter`'s own docstring records that under
 * `any_user` on a public board a **signed-in stranger** is admitted by the
 * commenting ladder and refused by the writer guard. Such a stranger can post a
 * comment, is therefore its author, and — with authorship as the only gate —
 * could write rows into a board's attachment section they may not otherwise
 * touch. `card_link` and `board_link` need only authorship, because a
 * `comment_attachments` row lives entirely inside the comment.
 *
 * ⚠ **`FR-COLLAB-005` AC3 is satisfied by there being no draft state at all.**
 * "Given I attach a file and then cancel the comment before posting … the file
 * is not added to the card": a composer draft is client state, cancelling means
 * this route is never called, and the presigned object left in the bucket is not
 * an attachment because no row names it. `board.renamed`'s docstring settles the
 * same class of criterion — "a client-side promise the server keeps trivially by
 * never being asked."
 */
export const commentAttachmentCreateBodySchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('file'),
    /**
     * The key from `POST /cards/:id/attachments/presign` on the comment's OWN
     * card. Same prefix check, same `HeadObject`, same reasons — see
     * `attachmentCreateBodySchema`. This route creates BOTH an `attachments` row
     * and a `comment_attachments` row, and emits `attachment.added` once.
     */
    storageKey: z.string().min(1).max(ATTACHMENT_URL_MAX_LENGTH),
    displayName: z.string().min(1).max(ATTACHMENT_DISPLAY_NAME_MAX_LENGTH).nullable(),
  }),
  z.strictObject({
    kind: z.literal('card_link'),
    /**
     * The caller must be able to READ this card. Refused with the same `404`
     * every card guard throws, so the route is not an existence oracle.
     * ⚠ **Emits nothing** — no `attachments` row is created, and the chip is
     * part of a comment whose creation `comment.added` already recorded. See
     * `./events`' B27 block for the symmetry argument with `comment.deleted`.
     */
    cardId: ulidSchema,
  }),
  z.strictObject({
    kind: z.literal('board_link'),
    /** The caller must be able to READ this board (`isBoardReadableBy`).
     * Emits nothing, for `card_link`'s reason. */
    boardId: ulidSchema,
  }),
]);

export type CommentAttachmentCreateBody = z.infer<
  typeof commentAttachmentCreateBodySchema
>;
